import { NavLink, Outlet } from "react-router-dom";
import { Users, Server, Radio, Settings as SettingsIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { env } from "../env.js";
import { useMe } from "../api/hooks.js";
import { Avatar } from "../components/ui.js";

const NAV = [
  { to: "/users", label: "Users", icon: Users },
  { to: "/nodes", label: "Nodes", icon: Server },
  { to: "/streams", label: "Streams", icon: Radio },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

function BackendIndicator() {
  // /api/me succeeding implies the backend is reachable.
  const me = useMe();
  const ok = me.isSuccess && me.data !== null;
  const color = me.isError
    ? "bg-destructive shadow-[0_0_8px] shadow-destructive/60"
    : ok
      ? "bg-emerald-400 shadow-[0_0_8px] shadow-emerald-500/60"
      : "bg-amber-400 shadow-[0_0_8px] shadow-amber-500/60";
  const label = me.isError ? "unreachable" : ok ? "online" : "connecting";
  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/[0.07] bg-black/20 px-3 py-2">
      <span className={cn("h-1.5 w-1.5 rounded-full", color)} />
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        backend · {label}
      </span>
    </div>
  );
}

function BrandMark() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary font-display text-base font-bold text-primary-foreground shadow-[0_6px_18px_-6px_hsl(var(--primary)/0.8)] ring-1 ring-white/20">
        m
      </span>
      <span className="flex items-baseline gap-1.5 text-sm font-semibold tracking-tight">
        <span className="font-display text-foreground">maitv</span>
        <span className="rounded border border-primary/30 bg-primary/15 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-primary">
          Admin
        </span>
      </span>
    </div>
  );
}

export function Layout() {
  const me = useMe();
  const account = me.data?.displayName ?? me.data?.username ?? "admin";

  return (
    <div className="flex h-screen flex-col text-foreground">
      {/* Top bar */}
      <header className="relative z-20 flex h-14 shrink-0 items-center justify-between border-b border-white/[0.07] bg-background/60 px-4 backdrop-blur-xl">
        <BrandMark />
        <div className="flex items-center gap-3 text-sm">
          <span className="hidden font-medium text-foreground/90 sm:inline">
            {account}
          </span>
          <Avatar src={me.data?.avatarUrl} name={account} className="h-8 w-8" />
        </div>
        {/* Thin lime transmission line under the header. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Sidebar */}
        <nav className="flex w-60 shrink-0 flex-col justify-between border-r border-white/[0.07] bg-background/30 px-3 py-5">
          <div>
            <p className="eyebrow px-3 pb-3">Control</p>
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "group relative mb-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary/[0.08] text-foreground"
                      : "text-muted-foreground hover:bg-white/5 hover:text-foreground",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        "absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary transition-all",
                        isActive
                          ? "opacity-100 shadow-[0_0_10px] shadow-primary/60"
                          : "opacity-0",
                      )}
                    />
                    <Icon
                      className={cn(
                        "h-4 w-4 transition-colors",
                        isActive
                          ? "text-primary"
                          : "text-muted-foreground group-hover:text-foreground",
                      )}
                    />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
          <div className="space-y-2">
            <BackendIndicator />
            <div className="px-1 font-mono text-[10px] tracking-wide text-muted-foreground/60">
              {env.appName}
            </div>
          </div>
        </nav>

        {/* Routed content */}
        <main className="scrollbar-thin min-w-0 flex-1 overflow-y-auto">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
